import { useState } from "react";
import { motion } from "framer-motion";
import {
  Shield, ArrowRight, ChevronDown, ChevronUp, ExternalLink,
  CheckCircle, Truck, Car, MapPin, Search,
  Handshake, FileCheck, CreditCard, Landmark, PackageCheck,
} from "lucide-react";
import SiteNav from "@/components/SiteNav";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663237234207/LgLeLvV4TKo2HFP6L5dQ2s/hero-passar-YiUiYu6DFoeikfz3wjJT7Q.webp";

/* ─── Fade-in animation wrapper ─── */
function FadeIn({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}


/* ─── Hero Section ─── */
function Hero() {
  const t = useTranslations("home");
  return (
    <section className="relative min-h-[90vh] flex items-end overflow-hidden pt-16">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Recherche de voiture en Suisse et en Europe" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
      </div>

      <div className="relative container pb-16 md:pb-24">
        <FadeIn>
          <div className="inline-flex items-center gap-2 bg-swiss-red/90 text-white px-4 py-1.5 text-xs font-semibold tracking-widest uppercase mb-6">
            <Shield size={14} />
            {t("hero.badge")}
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight max-w-4xl" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {t("hero.titleLine1")}
            <br />
            <span className="text-swiss-red">{t("hero.titleLine2")}</span>
          </h1>
        </FadeIn>

        <FadeIn delay={0.2}>
          <p className="mt-6 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">
            {t.rich("hero.descriptionRich", {
              strong: (chunks) => <strong className="text-white">{chunks}</strong>,
            })}
          </p>
        </FadeIn>

        <FadeIn delay={0.3}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link href="/mon-espace" className="inline-flex items-center gap-2 bg-swiss-red text-white px-6 py-3.5 font-semibold text-sm hover:bg-swiss-red/90 transition-colors">
              {t("hero.ctaSearch")} <ArrowRight size={16} />
            </Link>
            <Link href="/catalogue" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white border border-white/20 px-6 py-3.5 font-semibold text-sm hover:bg-white/20 transition-colors">
              {t("hero.ctaCatalogue")}
            </Link>
          </div>
        </FadeIn>

        {/* Country flags */}
        <FadeIn delay={0.4}>
          <div className="mt-16 flex flex-wrap items-center gap-3 sm:gap-6">
            {[
              { flag: "🇩🇪", key: "countryDE" as const },
              { flag: "🇫🇷", key: "countryFR" as const },
              { flag: "🇧🇪", key: "countryBE" as const },
              { flag: "🇳🇱", key: "countryNL" as const },
            ].map((c) => (
              <div key={c.key} className="flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-2 sm:px-4 sm:py-2.5">
                <span className="text-lg sm:text-xl">{c.flag}</span>
                <span className="text-xs font-medium text-white/70">{t(`hero.${c.key}`)}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-white/40">
              <ArrowRight size={16} />
              <span className="text-2xl">🇨🇭</span>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ─── Services Section ─── */
function Services() {
  const t = useTranslations("home");
  return (
    <section id="services" className="py-20 md:py-28 bg-offwhite">
      <div className="container">
        <FadeIn>
          <div className="text-center mb-16">
            <span className="text-swiss-red font-semibold text-sm tracking-widest uppercase">{t("services.sectionLabel")}</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold text-graphite leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {t("services.title")}
            </h2>
            <p className="mt-4 text-graphite/60 max-w-2xl mx-auto text-lg">
              {t("services.subtitle")}
            </p>
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* Service 1 — Sourcing sur commande */}
          <FadeIn delay={0.1}>
            <div className="bg-white border border-border p-8 md:p-10 h-full group hover:border-swiss-red/30 transition-colors">
              <div className="w-12 h-12 bg-swiss-red/10 text-swiss-red flex items-center justify-center mb-6">
                <Search size={24} />
              </div>
              <h3 className="text-2xl font-bold text-graphite mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {t("services.sourcing.title")}
              </h3>
              <p className="text-graphite/60 leading-relaxed mb-6">
                {t("services.sourcing.description")}
              </p>
              <ul className="space-y-3">
                {([1, 2, 3, 4] as const).map((n) => (
                  <li key={n} className="flex items-start gap-2.5 text-sm text-graphite/70">
                    <CheckCircle size={15} className="text-swiss-red shrink-0 mt-0.5" />
                    {t(`services.sourcing.item${n}`)}
                  </li>
                ))}
              </ul>
              <Link href="/login" className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-swiss-red hover:underline">
                {t("services.sourcing.cta")} <ArrowRight size={14} />
              </Link>
            </div>
          </FadeIn>

          {/* Service 2 — Catalogue */}
          <FadeIn delay={0.2}>
            <div className="bg-white border border-border p-8 md:p-10 h-full group hover:border-swiss-red/30 transition-colors">
              <div className="w-12 h-12 bg-swiss-red/10 text-swiss-red flex items-center justify-center mb-6">
                <Car size={24} />
              </div>
              <h3 className="text-2xl font-bold text-graphite mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {t("services.catalogue.title")}
              </h3>
              <p className="text-graphite/60 leading-relaxed mb-6">
                {t("services.catalogue.description")}
              </p>
              <ul className="space-y-3">
                {([1, 2, 3, 4] as const).map((n) => (
                  <li key={n} className="flex items-start gap-2.5 text-sm text-graphite/70">
                    <CheckCircle size={15} className="text-swiss-red shrink-0 mt-0.5" />
                    {t(`services.catalogue.item${n}`)}
                  </li>
                ))}
              </ul>
              <Link href="/catalogue" className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-swiss-red hover:underline">
                {t("services.catalogue.cta")} <ArrowRight size={14} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

/* ─── Process Steps ─── */
function ProcessSteps() {
  const t = useTranslations("home");
  const stepKeys = ["step1", "step2", "step3", "step4", "step5", "step6"] as const;
  const stepIcons = [
    <Search key="s1" size={24} />,
    <Handshake key="s2" size={24} />,
    <Truck key="s3" size={24} />,
    <CreditCard key="s4" size={24} />,
    <FileCheck key="s5" size={24} />,
    <Landmark key="s6" size={24} />,
  ];

  const steps = stepKeys.map((key, i) => ({
    num: String(i + 1).padStart(2, "0"),
    icon: stepIcons[i],
    title: t(`process.${key}.title`),
    desc: t(`process.${key}.desc`),
    details: [
      t(`process.${key}.detail1`),
      t(`process.${key}.detail2`),
      t(`process.${key}.detail3`),
      t(`process.${key}.detail4`),
    ],
  }));

  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <section id="processus" className="py-20 md:py-28 relative bg-dark-section">
      <div className="relative container">
        <FadeIn>
          <div className="text-center mb-16">
            <span className="text-swiss-red font-semibold text-sm tracking-widest uppercase">{t("process.sectionLabel")}</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold text-white leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {t("process.title")}
            </h2>
            <p className="mt-4 text-white/50 max-w-2xl mx-auto text-lg">
              {t("process.subtitle")}
            </p>
          </div>
        </FadeIn>

        <div className="max-w-4xl mx-auto space-y-4">
          {steps.map((step, i) => (
            <FadeIn key={step.num} delay={i * 0.08}>
              <div className={`border transition-colors ${expanded === i ? "border-swiss-red/50 bg-white/[0.06]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05]"}`}>
                <button onClick={() => setExpanded(expanded === i ? null : i)} className="w-full flex items-center gap-4 md:gap-6 px-5 md:px-8 py-5 md:py-6 text-left">
                  <span className="text-3xl md:text-5xl font-bold text-swiss-red/30" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{step.num}</span>
                  <div className="flex-shrink-0 w-10 h-10 bg-swiss-red/20 text-swiss-red flex items-center justify-center">{step.icon}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg md:text-xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{step.title}</h3>
                    <p className="text-sm text-white/50 mt-1 line-clamp-1 md:line-clamp-none">{step.desc}</p>
                  </div>
                  <div className="flex-shrink-0 text-white/40">{expanded === i ? <ChevronUp size={20} /> : <ChevronDown size={20} />}</div>
                </button>
                {expanded === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} transition={{ duration: 0.3 }} className="px-5 md:px-8 pb-6">
                    <div className="border-t border-white/10 pt-5 ml-0 md:ml-[7.5rem]">
                      <ul className="space-y-3">
                        {step.details.map((d, j) => (
                          <li key={j} className="flex items-start gap-3">
                            <CheckCircle size={16} className="flex-shrink-0 text-swiss-red mt-0.5" />
                            <span className="text-sm text-white/70 leading-relaxed">{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CO2 penalty (BFE/SFOE formula for individual imports, 2025) ─── */
const CO2_BASE_TARGET = 93.6;  // g/km base target 2025 (WLTP)
const CO2_REF_WEIGHT = 1767;   // kg reference weight 2025
const CO2_WEIGHT_FACTOR = 0.0333; // g/km per kg above/below ref weight
const CO2_RATE = 95; // CHF per g/km exceedance

function calcCo2Target(weightKg: number): number {
  return CO2_BASE_TARGET + CO2_WEIGHT_FACTOR * (weightKg - CO2_REF_WEIGHT);
}

function calcCo2Penalty(gkm: number, weightKg: number): number {
  const target = calcCo2Target(weightKg);
  const excess = gkm - target;
  if (excess <= 0) return 0;
  return Math.round(excess * CO2_RATE);
}

/* ─── Tax Calculator ─── */
type Currency = "CHF" | "EUR";
const DEFAULT_EUR_CHF = 0.94;

function TaxCalculator() {
  const t = useTranslations("home");
  const [price, setPrice] = useState("");
  const [transport, setTransport] = useState("");
  const [co2, setCo2] = useState("");
  const [weight, setWeight] = useState("");
  const [co2Exempt, setCo2Exempt] = useState(false);
  const [currency, setCurrency] = useState<Currency>("CHF");
  const [eurRate, setEurRate] = useState(DEFAULT_EUR_CHF.toString());

  const rate = parseFloat(eurRate) || DEFAULT_EUR_CHF;
  const toChf = (v: number) => currency === "EUR" ? v * rate : v;
  const priceNum = toChf(parseFloat(price) || 0);
  const transportNum = toChf(parseFloat(transport) || 0);
  const co2Num = parseFloat(co2) || 0;
  const weightNum = parseFloat(weight) || CO2_REF_WEIGHT;
  const co2Target = calcCo2Target(weightNum);
  const autoTax = priceNum * 0.04;
  const tvaBase = priceNum + autoTax + transportNum + 20;
  const tva = tvaBase * 0.081;
  const co2Penalty = co2Exempt ? 0 : calcCo2Penalty(co2Num, weightNum);
  const total = autoTax + tva + 20 + co2Penalty;

  return (
    <section id="taxes" className="py-20 md:py-28 bg-offwhite">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <FadeIn>
            <div>
              <span className="text-swiss-red font-semibold text-sm tracking-widest uppercase">{t("calculator.sectionLabel")}</span>
              <h2 className="mt-3 text-3xl md:text-5xl font-bold text-graphite leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {t("calculator.title")}
              </h2>
              <p className="mt-4 text-graphite/60 text-lg">
                {t("calculator.subtitle")}
              </p>
              <div className="mt-8 space-y-5">
                <div className="flex items-center gap-2">
                  <button onClick={() => setCurrency("CHF")}
                    className={`px-4 py-2 text-sm font-semibold transition-colors ${currency === "CHF" ? "bg-graphite text-white" : "bg-white border border-border text-graphite/60 hover:text-graphite"}`}>
                    CHF
                  </button>
                  <button onClick={() => setCurrency("EUR")}
                    className={`px-4 py-2 text-sm font-semibold transition-colors ${currency === "EUR" ? "bg-graphite text-white" : "bg-white border border-border text-graphite/60 hover:text-graphite"}`}>
                    EUR
                  </button>
                  {currency === "EUR" && (
                    <div className="flex items-center gap-2 ml-auto">
                      <span className="text-xs text-graphite/50">1 EUR =</span>
                      <input type="number" step="0.01" value={eurRate} onChange={(e) => setEurRate(e.target.value)}
                        className="w-20 border border-border bg-white px-2 py-1.5 text-sm text-graphite text-center focus:border-swiss-red focus:ring-1 focus:ring-swiss-red outline-none transition-colors" />
                      <span className="text-xs text-graphite/50">CHF</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-graphite mb-2">{t("calculator.vehicleValue", { currency })}</label>
                  <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={t("calculator.vehicleValuePlaceholder")}
                    className="w-full border border-border bg-white px-4 py-3 text-graphite placeholder:text-graphite/30 focus:border-swiss-red focus:ring-1 focus:ring-swiss-red outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-graphite mb-2">{t("calculator.transportCost", { currency })}</label>
                  <input type="number" value={transport} onChange={(e) => setTransport(e.target.value)} placeholder={t("calculator.transportCostPlaceholder")}
                    className="w-full border border-border bg-white px-4 py-3 text-graphite placeholder:text-graphite/30 focus:border-swiss-red focus:ring-1 focus:ring-swiss-red outline-none transition-colors" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-graphite mb-2">{t("calculator.co2Emissions")}</label>
                    <input type="number" value={co2} onChange={(e) => setCo2(e.target.value)} placeholder={t("calculator.co2EmissionsPlaceholder")}
                      className="w-full border border-border bg-white px-4 py-3 text-graphite placeholder:text-graphite/30 focus:border-swiss-red focus:ring-1 focus:ring-swiss-red outline-none transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-graphite mb-2">{t("calculator.emptyWeight")}</label>
                    <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder={t("calculator.emptyWeightPlaceholder")}
                      className="w-full border border-border bg-white px-4 py-3 text-graphite placeholder:text-graphite/30 focus:border-swiss-red focus:ring-1 focus:ring-swiss-red outline-none transition-colors" />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={co2Exempt} onChange={(e) => setCo2Exempt(e.target.checked)}
                    className="accent-swiss-red w-4 h-4" />
                  <span className="text-xs text-graphite/50">{t("calculator.co2Exempt")}</span>
                </label>
              </div>
              <div className="mt-8 border border-border bg-white">
                {currency === "EUR" && priceNum > 0 && (
                  <div className="px-6 py-3 border-b border-border bg-graphite/5 flex justify-between items-center">
                    <span className="text-xs text-graphite/50">{t("calculator.convertedValue", { rate })}</span>
                    <span className="text-xs font-medium text-graphite/60" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{priceNum.toFixed(2)} CHF</span>
                  </div>
                )}
                <div className="px-6 py-4 border-b border-border flex justify-between items-center">
                  <span className="text-sm text-graphite/60">{t("calculator.autoTax")}</span>
                  <span className="font-semibold text-graphite" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{autoTax.toFixed(2)} CHF</span>
                </div>
                <div className="px-6 py-4 border-b border-border flex justify-between items-center">
                  <span className="text-sm text-graphite/60">{t("calculator.vat")}</span>
                  <span className="font-semibold text-graphite" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{tva.toFixed(2)} CHF</span>
                </div>
                <div className="px-6 py-4 border-b border-border flex justify-between items-center">
                  <span className="text-sm text-graphite/60">{t("calculator.form1320A")}</span>
                  <span className="font-semibold text-graphite" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>20.00 CHF</span>
                </div>
                {co2Num > 0 && (
                  <div className="px-6 py-4 border-b border-border flex justify-between items-center">
                    <span className="text-sm text-graphite/60">
                      {t("calculator.co2Penalty")} {co2Exempt ? <span className="text-green-600 ml-1">({t("calculator.co2PenaltyExempt")})</span> : co2Num <= co2Target ? <span className="text-green-600 ml-1">({t("calculator.co2PenaltyBelowTarget", { target: co2Target.toFixed(1) })})</span> : <span className="text-swiss-red ml-1">({t("calculator.co2PenaltyAboveTarget", { excess: Math.ceil(co2Num - co2Target), rate: CO2_RATE })})</span>}
                    </span>
                    <span className={`font-semibold ${co2Penalty > 0 ? "text-swiss-red" : "text-graphite"}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{co2Penalty.toFixed(2)} CHF</span>
                  </div>
                )}
                <div className="px-6 py-5 bg-graphite flex justify-between items-center">
                  <span className="text-sm text-white/70 font-semibold">{t("calculator.totalTaxes")}</span>
                  <span className="text-2xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{total.toFixed(2)} CHF</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-graphite/40">{t("calculator.disclaimer", { baseTarget: CO2_BASE_TARGET, rate: CO2_RATE })}</p>
            </div>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="bg-white border border-border p-8">
              <h3 className="font-bold text-graphite text-lg mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t("calculator.infoTitle")}</h3>
              <div className="space-y-6">
                {([
                  { titleKey: "calculator.info1Title", descKey: "calculator.info1Desc" },
                  { titleKey: "calculator.info2Title", descKey: "calculator.info2Desc" },
                  { titleKey: "calculator.info3Title", descKey: "calculator.info3Desc" },
                ] as const).map((item) => (
                  <div key={item.titleKey}>
                    <h4 className="font-semibold text-graphite text-sm">{t(item.titleKey)}</h4>
                    <p className="text-sm text-graphite/60 mt-1 leading-relaxed">{t(item.descKey, { rate: CO2_RATE, baseTarget: CO2_BASE_TARGET })}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

/* ─── Countries Section ─── */
function Countries() {
  const t = useTranslations("home");
  const countryKeys = ["de", "fr", "be", "nl"] as const;
  const flags = { de: "🇩🇪", fr: "🇫🇷", be: "🇧🇪", nl: "🇳🇱" };

  const countries = countryKeys.map((key) => ({
    flag: flags[key],
    name: t(`countries.${key}.name`),
    desc: t(`countries.${key}.desc`),
    cities: t(`countries.${key}.cities`),
  }));

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="container">
        <FadeIn>
          <div className="text-center mb-16">
            <span className="text-swiss-red font-semibold text-sm tracking-widest uppercase">{t("countries.sectionLabel")}</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold text-graphite leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {t("countries.title")}
            </h2>
          </div>
        </FadeIn>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {countries.map((c, i) => (
            <FadeIn key={c.name} delay={i * 0.08}>
              <div className="border border-border p-6 h-full hover:border-swiss-red/30 transition-colors">
                <span className="text-4xl mb-4 block">{c.flag}</span>
                <h3 className="text-lg font-bold text-graphite mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{c.name}</h3>
                <p className="text-sm text-graphite/60 leading-relaxed mb-4">{c.desc}</p>
                <div className="flex items-center gap-1.5 text-xs text-graphite/35">
                  <MapPin size={12} />
                  {c.cities}
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ Section ─── */
function FAQ() {
  const t = useTranslations("home");
  const [open, setOpen] = useState<number | null>(null);
  const faqKeys = [1, 2, 3, 4, 5, 6] as const;
  const faqs = faqKeys.map((n) => ({
    q: t(`faq.q${n}`),
    a: t(`faq.a${n}`),
  }));

  return (
    <section id="faq" className="py-20 md:py-28 bg-offwhite">
      <div className="container max-w-4xl">
        <FadeIn>
          <div className="text-center mb-16">
            <span className="text-swiss-red font-semibold text-sm tracking-widest uppercase">{t("faq.sectionLabel")}</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold text-graphite leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t("faq.title")}</h2>
          </div>
        </FadeIn>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <FadeIn key={i} delay={i * 0.05}>
              <div className="border border-border bg-white">
                <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between px-6 py-5 text-left">
                  <span className="font-semibold text-graphite pr-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{faq.q}</span>
                  <div className="flex-shrink-0 text-swiss-red">{open === i ? <ChevronUp size={20} /> : <ChevronDown size={20} />}</div>
                </button>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} transition={{ duration: 0.3 }} className="px-6 pb-5">
                    <p className="text-graphite/60 leading-relaxed border-t border-border pt-4">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA + Footer ─── */
function Footer() {
  const t = useTranslations("home");
  const ft = useTranslations("footer");
  return (
    <>
      <section className="py-16 md:py-20 bg-dark-section">
        <div className="container text-center">
          <FadeIn>
            <PackageCheck size={32} className="text-swiss-red mx-auto mb-4" />
            <h2 className="text-2xl md:text-4xl font-bold text-white max-w-3xl mx-auto leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {t("cta.title")}
            </h2>
            <p className="mt-4 text-white/50 max-w-xl mx-auto">
              {t("cta.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link href="/catalogue" className="inline-flex items-center gap-2 bg-swiss-red text-white px-6 py-3.5 font-semibold text-sm hover:bg-swiss-red/90 transition-colors">
                {t("cta.ctaCatalogue")} <ArrowRight size={14} />
              </Link>
              <Link href="/login" className="inline-flex items-center gap-2 bg-white/10 text-white border border-white/20 px-6 py-3.5 font-semibold text-sm hover:bg-white/20 transition-colors">
                {t("cta.ctaImport")} <ArrowRight size={14} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <footer className="bg-graphite py-12">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-7 h-7 bg-swiss-red flex items-center justify-center">
                  <span className="text-white font-bold text-xs" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>H</span>
                </div>
                <span className="font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Helvetic<span className="text-swiss-red">Cars</span>
                </span>
              </div>
              <p className="text-sm text-white/40 leading-relaxed">
                {ft("description")}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{ft("navigation")}</h4>
              <div className="space-y-2">
                <Link href="/catalogue" className="flex items-center gap-1.5 text-sm text-white/50 hover:text-swiss-red transition-colors">
                  {ft("catalogue")}
                </Link>
                <Link href="/login" className="flex items-center gap-1.5 text-sm text-white/50 hover:text-swiss-red transition-colors">
                  {ft("espaceMembre")}
                </Link>
                <a href="https://www.bazg.admin.ch/fr" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-white/50 hover:text-swiss-red transition-colors">
                  {ft("ofdf")} <ExternalLink size={12} />
                </a>
                <a href="https://www.eportal.admin.ch/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-white/50 hover:text-swiss-red transition-colors">
                  {ft("ePortal")} <ExternalLink size={12} />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{ft("paysCouvertsTitle")}</h4>
              <div className="space-y-2 text-sm text-white/50">
                <p>🇩🇪 {ft("paysCouvertsDE")}</p>
                <p>🇫🇷 {ft("paysCouvertsFR")}</p>
                <p>🇧🇪 {ft("paysCouvertsBE")}</p>
                <p>🇳🇱 {ft("paysCouvertsNL")}</p>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-white/30">{ft("copyright")}</p>
            <p className="text-xs text-white/30">{ft("customsInfo")}</p>
          </div>
        </div>
      </footer>
    </>
  );
}

/* ─── Main Page ─── */
export default function Home() {
  return (
    <div className="min-h-screen">
      <SiteNav />
      <Hero />
      <Services />
      <ProcessSteps />
      <TaxCalculator />
      <Countries />
      <FAQ />
      <Footer />
    </div>
  );
}
